import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";

import LeadAssignApi from "../../api/LeadAssignApi";
import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";

import { showSuccess, showError } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";

function List() {
  const [list, setList] = useState([]);
  const [editData, setEditData] = useState(null);
  const [deleteData, setDeleteData] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ================= AUTH / RBAC ================= */
  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  /* ================= LOAD DATA ================= */
  const loadData = useCallback(async () => {
    try {
      const res = await LeadAssignApi.list();
      if (res.code === 200) {
        setList(res.data);
      } else {
        showError(res.message);
      }
    } catch {
      showError("Failed to load lead settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (can(permissions, "read", "leadAssign")) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [permissions]);

  /* ================= AUTH GUARD ================= */
  if (!can(permissions, "read", "leadAssign")) {
    return <Navigate to="/403" replace />;
  }

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (!deleteData) return;

    if (!can(permissions, "delete", "leadAssign")) {
      return showError("Access Denied");
    }

    try {
      const res = await LeadAssignApi.delete({ id: deleteData._id });
      if (res.code === 200) {
        showSuccess(res.message);

        window.bootstrap
          ?.Modal?.getInstance(
            document.getElementById("delete-leadassign-modal")
          )
          ?.hide();

        setDeleteData(null);
        loadData();
      } else {
        showError(res.message);
      }
    } catch {
      showError("Delete failed");
    }
  };

  /* ================= LOADER ================= */
  if (loading) {
    return (
      <div className="p-5 text-center">
        <div className="spinner-border text-primary mb-2"></div>
        <h4>Loading Settings...</h4>
      </div>
    );
  }

  return (
    <div className="content pb-0">
      {/* ================= HEADER (UI from old code) ================= */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-1">
          Lead Setting
          <span className="badge badge-soft-primary ms-2">
            {list.length}
          </span>
        </h4>

        {can(permissions, "write", "leadAssign") && (
          <button
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#add-leadassign-modal"
          >
            <i className="ti ti-square-rounded-plus-filled me-1" />
            Lead Setting
          </button>
        )}
      </div>

      {/* ================= TABLE (UI from old code) ================= */}
      <div className="card shadow-sm border-0">
        <div className="card-body table-responsive">
          <table className="table table-nowrap align-middle">
            <thead className="table-light">
              <tr>
                <th>S.no</th>
                <th>Manager</th>
                <th>Type</th>
                <th>Source</th>
                <th>Group</th>
                <th>District</th>
                <th>Pincode</th>
                {(can(permissions, "write", "leadAssign") ||
                  can(permissions, "delete", "leadAssign")) && (
                  <th className="text-center">Action</th>
                )}
              </tr>
            </thead>

            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No rules created
                  </td>
                </tr>
              ) : (
                list.map((i, idx) => (
                  <tr key={i._id}>
                    <td>{idx + 1}</td>
                    <td className="fw-bold">
                      {i.managerId?.name || "-"}
                    </td>
                    <td>{i.typeId?.type || "-"}</td>
                    <td>{i.sourceId?.source || "-"}</td>
                    <td>{i.groupId?.group || "-"}</td>
                    <td>{i.district || "-"}</td>
                    <td>{i.pincode || "-"}</td>

                    <td className="text-center">
                      <div className="d-flex gap-2 justify-content-center">
                        {can(permissions, "write", "leadAssign") && (
                          <button
                            className="btn btn-sm btn-success"
                            data-bs-toggle="modal"
                            data-bs-target="#edit-leadassign-modal"
                            onClick={() => setEditData(i)}
                          >
                            <i className="ti ti-edit" />
                          </button>
                        )}

                        {can(permissions, "delete", "leadAssign") && (
                          <button
                            className="btn btn-sm btn-danger"
                            data-bs-toggle="modal"
                            data-bs-target="#delete-leadassign-modal"
                            onClick={() => setDeleteData(i)}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODALS ================= */}
      <div className="modal fade" id="add-leadassign-modal">
        <AddDetails onSuccess={loadData} />
      </div>

      <div className="modal fade" id="edit-leadassign-modal">
        <EditDetails data={editData} onSuccess={loadData} />
      </div>

      <div className="modal fade" id="delete-leadassign-modal">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-body p-4 text-center">
              <h5>Delete Confirmation</h5>
              <p>Are you sure?</p>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light w-100"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger w-100"
                  onClick={handleDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
